import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getProject, deleteProject } from '../api/client'
import type { ForecastRun, ProjectDetail } from '../types'
import ProbabilityChart from '../components/ProbabilityChart'
import EditProjectModal from '../components/EditProjectModal'
import { useSessionStore } from '../store/sessionStore'
import { useUIStore } from '../store/uiStore'

export default function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const isAdmin = useSessionStore((s) => s.isAdmin)
  const openModal = useUIStore((s) => s.openModal)
  const [project, setProject] = useState<ProjectDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const loadProject = async () => {
    if (!projectId) return
    setIsLoading(true)
    try {
      const p = await getProject(projectId)
      setProject(p)
    } catch (e) {
      console.error('Failed to load project:', e)
      alert(`Failed to load project: ${e instanceof Error ? e.message : 'Unknown error'}`)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void loadProject()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId])

  const handleDelete = async () => {
    if (!projectId) return
    const ok = confirm(`Delete project "${project?.name}"? Questions will be unlinked but not deleted.`)
    if (!ok) return
    try {
      await deleteProject(projectId)
      navigate('/projects')
    } catch (e) {
      console.error('Failed to delete project:', e)
      alert(`Failed to delete project: ${e instanceof Error ? e.message : 'Unknown error'}`)
    }
  }

  const runsByQuestion = useMemo(() => {
    const map = new Map<string, ForecastRun[]>()
    for (const r of project?.runs || []) {
      const qid = r.question_id || ''
      if (!qid) continue
      const prev = map.get(qid) || []
      map.set(qid, [...prev, r])
    }
    // Ensure newest-first ordering within each question (backend already does, but be safe)
    for (const [qid, rs] of map.entries()) {
      map.set(
        qid,
        [...rs].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
      )
    }
    return map
  }, [project?.runs])

  if (isLoading) {
    return (
      <div className="min-h-screen px-4 sm:px-6 py-6 sm:py-8">
        <div className="mx-auto max-w-7xl font-mono text-xs text-muted">Loading…</div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="min-h-screen px-4 sm:px-6 py-6 sm:py-8">
        <div className="mx-auto max-w-7xl text-sm text-muted">Project not found.</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6 sm:py-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-serif font-semibold text-2xl sm:text-3xl text-ink">{project.name}</h1>
            <div className="mt-1 text-sm text-muted max-w-prose">{project.description || 'No description.'}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {isAdmin && (
              <>
                <button
                  className="rounded-xl border border-rule bg-paper shadow-delphi px-3 py-2 text-sm font-semibold text-ink hover:bg-white/40 transition"
                  type="button"
                  onClick={() => openModal('editProject')}
                >
                  Edit
                </button>
                <button
                  className="rounded-xl border border-rule bg-paper shadow-delphi px-3 py-2 text-sm font-semibold text-ink hover:bg-white/40 transition"
                  type="button"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </>
            )}
            <button
              className="rounded-xl border border-rule bg-paper shadow-delphi px-3 py-2 text-sm font-semibold text-ink hover:bg-white/40 transition"
              type="button"
              onClick={() => navigate('/projects')}
            >
              ← Back to projects
            </button>
          </div>
        </div>

        {(project.questions || []).length === 0 ? (
          <div className="mt-4 rounded-delphi border border-rule bg-paper2 shadow-delphi p-4 text-sm text-muted">
            No forecasts in this project yet.
          </div>
        ) : (
          (project.questions || []).map((q) => {
            const runs = runsByQuestion.get(q.question_id) || []
            return (
              <section key={q.question_id} className="mt-4 rounded-delphi border border-rule bg-paper2 shadow-delphi p-4 sm:p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-serif font-semibold text-ink leading-snug">{q.formatted_question}</div>
                    {q.resolution_criteria && (
                      <div className="mt-2 text-sm text-muted line-clamp-3">{q.resolution_criteria}</div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="font-mono text-[11px] text-muted border border-rule rounded-full px-2 py-1 bg-paper">
                      {q.run_count ?? runs.length} runs
                    </span>
                    {q.last_run_at && (
                      <span className="font-mono text-[11px] text-muted border border-rule rounded-full px-2 py-1 bg-paper">
                        Last run: {new Date(q.last_run_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>

                {runs.length > 0 && (
                  <div className="mt-4 rounded-delphi border border-rule bg-paper shadow-delphi p-3 sm:p-4">
                    <ProbabilityChart runs={runs} userPredictions={[]} height={240} />
                  </div>
                )}

                <div className="mt-4">
                  <button
                    className="rounded-xl px-4 py-2.5 bg-green text-white font-semibold text-sm shadow-delphi hover:brightness-[0.98] transition"
                    type="button"
                    onClick={() => navigate(`/results/${q.question_id}`)}
                  >
                    Open forecast
                  </button>
                </div>
              </section>
            )
          })
        )}
      </div>

      {projectId && (
        <EditProjectModal
          projectId={projectId}
          initialName={project?.name || ''}
          initialDescription={project?.description || ''}
          onUpdated={loadProject}
        />
      )}
    </div>
  )
}

